/**
 * SearchList Page
 * 
 * This page displays a list of farms based on search query.
 * It fetches farms by text search and allows pagination.
 * 
 * @author FarmCiti Team
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SearchListPageStyle from "../../assets/stylesheets/search-list/SearchListPage.module.css";
import { searchFarmsByText } from "../../api/search-service/searchService"
import HeaderStyle from "../../assets/stylesheets/Header.module.css";
import FarmCardList from "../../components/farm/FarmCardList";
import Pagination from "../../components/common/Pagination";
import ListHeader from "../../layouts/ListHeader";
import Logger from "../../utils/logger";
import slugify from 'slugify';

const log = Logger.create('SearchList');

const LIMIT = 20;

/**
 * SearchList component displays a list of farms based on search query.
 * @returns {JSX.Element} The rendered component.
 */
export default function SearchList() {
  const navigate = useNavigate();
  const locationObj = useLocation();
  const [farms, setFarms] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({});
  const [loading, setLoading] = useState(false);

  // Read initial page from URL
  const getInitialPage = () => {
    const params = new URLSearchParams(locationObj.search);
    return parseInt(params.get('page'), 10) || 1;
  };

  // Read search query from URL
  const getInitialQuery = () => {
    const params = new URLSearchParams(locationObj.search);
    const query = params.get('q') || '';
    return query;
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage());
  
  // Add search state
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState(getInitialQuery());
  const [hoveredFarmId, setHoveredFarmId] = useState(null);

  // Function to handle text search
  const handleTextSearch = useCallback(async (query, page = 1) => {
    setLoading(true);
    setIsSearchMode(true);
    setSearchQuery(query);
    
    try {
      const response = await searchFarmsByText({
        query: query,
        page: page,
        limit: LIMIT
      });
      
      const safeFarms = Array.isArray(response.items) ? response.items : [];
      setFarms(safeFarms);
      setPaginationMeta(response.meta || {});
      setCurrentPage(page);
      
      // Update URL with search query
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('page', page);
      const newUrl = `/search?${params.toString()}`;
      navigate(newUrl, { replace: true });
    } catch (error) {
      log.error('Error searching farms by text:', error);
      setFarms([]);
      setPaginationMeta({});
    } finally {
      setLoading(false);
    }
  }, [navigate]); // locationObj 제거

  // Clear search
  const clearSearch = useCallback(() => {
    setIsSearchMode(false);
    setSearchQuery('');
    setCurrentPage(1);
    setFarms([]);
    setPaginationMeta({});
    setLoading(false);
    
    // Remove query from URL
    const params = new URLSearchParams(locationObj.search);
    params.delete('q');
    params.set('page', '1');
    const newUrl = `${locationObj.pathname}?${params.toString()}`;
    navigate(newUrl, { replace: true });
  }, [locationObj, navigate]);

  // Function to handle page change
  const handlePageChange = useCallback((page) => {    
    if (isSearchMode && searchQuery) {
      // If in search mode, continue with text search
      handleTextSearch(searchQuery, page);
    }
    
    setCurrentPage(page);
    // Update page in URL
    const params = new URLSearchParams(locationObj.search);
    params.set('page', page);
    navigate(`${locationObj.pathname}?${params.toString()}`, { replace: true });
    
    // Scroll to top of the farm list when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isSearchMode, searchQuery, handleTextSearch, locationObj, navigate]);

  // Auto-search when URL has query parameter
  useEffect(() => {
    const params = new URLSearchParams(locationObj.search);
    const urlQuery = params.get('q');
    const urlPage = parseInt(params.get('page'), 10) || 1;
    
    if (urlQuery && urlQuery.trim()) {
      handleTextSearch(urlQuery, urlPage);
    } else {
      log.log(' No query in URL, skipping auto-search');
    }
  }, [locationObj.search, handleTextSearch]);

  return (
    <>

      
      <section className={`${SearchListPageStyle["search-list-section"]}`}>
        <div className={SearchListPageStyle["search-list-container"]}>
          <div className={SearchListPageStyle["search-list-main"]}>
            <div className={SearchListPageStyle["search-list-card-container"]}>
              <h1 className={SearchListPageStyle["search-list-title"]}>
                {isSearchMode ? (
                  `Search Results for "${searchQuery}"`
                ) : (
                  "Search Farms"
                )}
              </h1>

              <FarmCardList
                farms={farms}
                style={SearchListPageStyle["search-list-cards"]}
                loading={loading}
                onFarmClick={farm => {
                  const slug = slugify(farm.name || '', { lower: true, strict: true });
                  navigate(`/farm/${farm.id}/${slug}`);
                }}
                onFarmHover={(farmId) => setHoveredFarmId(farmId)}
                onFarmUnhover={() => setHoveredFarmId(null)}
              />
              
              {/* Pagination */}
              {!loading && paginationMeta && paginationMeta.total > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={paginationMeta.pages || 1}
                  totalItems={paginationMeta.total || 0}
                  itemsPerPage={LIMIT}
                  onPageChange={handlePageChange}
                  loading={loading}
                />
              )}
            </div>
          </div>
        </div>
        
        {/* Mobile navigation spacer */}
        <div className={HeaderStyle["mobile-header-container"]} style={{ visibility: 'hidden' }}></div>
      </section>
    </>
  );
}
